/**
 * `/integrations` — connected calendars.
 *
 * Screen 24 in the mockup sits at `/site/integrations`, which is the Site Administrator's view of
 * deployment-wide providers and arrives with Stage 9. This is the other half of the same feature:
 * the **Account-owned** connection, which is what a parish secretary actually has (L3-29 §11).
 *
 * The sentence the screen rests on:
 *
 * > Your calendar shows that something is happening and when. The planning stays here.
 *
 * That is a promise about what leaves the building, so the screen says plainly what an event will
 * contain — and shows it, rather than asking anybody to take it on trust.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import {
  beginAuthorization,
  chooseCalendar,
  disconnect,
  fetchCalendars,
  fetchIntegrations,
  fetchSyncState,
  type Connection,
} from './api';
import './integrations.css';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Connecting',
  active: 'Connected',
  authorization_required: 'Needs reconnection',
  error: 'Not working',
  revoked: 'Disconnected',
  disabled: 'Turned off',
};

export function IntegrationsScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const integrations = useQuery({ queryKey: ['integrations'], queryFn: fetchIntegrations });
  const connection = integrations.data?.data ?? null;

  const connect = useMutation({
    mutationFn: () => beginAuthorization('/integrations'),
    onSuccess: (result) => {
      // A full navigation, not a fetch: the consent screen is Google's page and belongs in the
      // address bar, where somebody can see whose it is before granting anything.
      window.location.assign(result.authorization_url);
    },
  });

  const error = [connect.error, integrations.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  return (
    <div className="int">
      <header className="int__head">
        <div>
          <h1 className="int__title">{t('integrations.title')}</h1>
          <p className="int__lede">{t('integrations.lede')}</p>
        </div>
        <Link className="int__link" to="/liturgies">
          {t('nav.liturgies')} →
        </Link>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      {integrations.data && !integrations.data.enabled ? (
        <section className="int__card">
          <p className="int__eyebrow">{t('integrations.not_enabled_title')}</p>
          <p className="int__body">{t('integrations.not_enabled_desc')}</p>
        </section>
      ) : (
        <div className="int__grid">
          <div className="int__main">
            {connection ? (
              <ConnectedCard
                connection={connection}
                confirming={confirmingDisconnect}
                onConfirm={setConfirmingDisconnect}
                onChanged={async () => {
                  await queryClient.invalidateQueries({ queryKey: ['integrations'] });
                  setConfirmingDisconnect(false);
                }}
              />
            ) : (
              <section className="int__card">
                <p className="int__eyebrow">{t('integrations.google_calendar')}</p>
                <p className="int__body">{t('integrations.google_calendar_desc')}</p>
                <button
                  type="button"
                  className="int__btn"
                  disabled={connect.isPending}
                  onClick={() => connect.mutate()}
                >
                  {connect.isPending
                    ? t('integrations.opening_google')
                    : `${t('integrations.connect')} Google Calendar`}
                </button>
                <p className="int__fine">{t('integrations.permissions_fine')}</p>
              </section>
            )}
          </div>

          <aside className="int__side">
            <section className="int__card int__card--warm">
              <p className="int__eyebrow">{t('integrations.what_event_says_title')}</p>
              <div className="int__sample">
                <div className="int__sample-title">{t('integrations.wedding_sample')}</div>
                <div className="int__sample-meta">{t('integrations.wedding_time')}</div>
                <div className="int__sample-meta">{t('integrations.sample_place')}</div>
                <div className="int__sample-body">
                  {t('integrations.sample_body_line1')}
                  <br />
                  {t('integrations.sample_body_line2')}
                </div>
              </div>
              <p className="int__fine">{t('integrations.sample_fine')}</p>
            </section>

            <section className="int__card">
              <p className="int__eyebrow">{t('integrations.titles_eyebrow')}</p>
              <p className="int__body">{t('integrations.titles_desc')}</p>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

function ConnectedCard({
  connection,
  confirming,
  onConfirm,
  onChanged,
}: {
  connection: Connection;
  confirming: boolean;
  onConfirm: (value: boolean) => void;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const calendars = useQuery({
    queryKey: ['integration-calendars', connection.id],
    queryFn: () => fetchCalendars(connection.id),
    // Pointless while the provider is refusing us; the user needs to reconnect first.
    enabled: !connection.needs_reconnection,
  });

  const sync = useQuery({
    queryKey: ['integration-sync', connection.id],
    queryFn: () => fetchSyncState(connection.id),
  });

  const pick = useMutation({
    mutationFn: (calendarId: string) => chooseCalendar(connection.id, calendarId),
    onSuccess: onChanged,
  });

  const cut = useMutation({
    mutationFn: (events: 'keep' | 'remove') => disconnect(connection.id, events),
    onSuccess: onChanged,
  });

  return (
    <>
      <section className="int__card">
        <div className="int__row">
          <div>
            <p className="int__eyebrow">{t('integrations.google_calendar')}</p>
            <p className="int__body">{connection.account ?? 'Connected account'}</p>
          </div>
          <span className={`int-pill int-pill--${connection.status}`}>
            {STATUS_LABEL[connection.status] ?? connection.status}
          </span>
        </div>

        {connection.last_error_code ? (
          <p className="int__alert">
            {t(
              `integrations.errors.${connection.last_error_code}`,
              t('integrations.errors.generic_error'),
            )}
          </p>
        ) : null}

        {connection.needs_reconnection ? null : (
          <label className="int__field">
            <span className="int__field-label">{t('integrations.calendar_label')}</span>
            <select
              value={connection.calendar_id ?? ''}
              onChange={(e) => pick.mutate(e.target.value)}
              disabled={calendars.isPending || pick.isPending}
            >
              <option value="" disabled>
                {calendars.isPending
                  ? t('integrations.loading')
                  : t('integrations.choose_calendar')}
              </option>
              {(calendars.data?.data ?? []).map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.name}
                </option>
              ))}
            </select>
            <span className="int__fine">{t('integrations.calendar_fine')}</span>
          </label>
        )}
      </section>

      <section className="int__card">
        <p className="int__eyebrow">{t('integrations.sync_eyebrow')}</p>
        {sync.isPending ? (
          <p className="int__fine">{t('integrations.loading')}</p>
        ) : sync.data?.data.length === 0 ? (
          <p className="int__fine">{t('integrations.sync_empty')}</p>
        ) : (
          <ul className="int__list">
            {(sync.data?.data ?? []).map((row) => (
              <li key={row.liturgy_id} className="int__sync">
                <Link
                  className="int__sync-link"
                  to="/liturgies/$id"
                  params={{ id: row.liturgy_id }}
                >
                  {row.liturgy_id.slice(0, 8)}
                </Link>
                <span className={`int-pill int-pill--${row.status}`}>{row.status}</span>
                <span className="int__fine">
                  {row.last_error_code
                    ? t(`integrations.errors.${row.last_error_code}`, row.last_error_code)
                    : row.last_synced_at
                      ? t('integrations.sync_synced_at', {
                          time: row.last_synced_at.slice(0, 16).replace('T', ' '),
                        })
                      : t('integrations.sync_not_yet')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="int__card">
        <p className="int__eyebrow">{t('integrations.disconnect_eyebrow')}</p>

        {!confirming ? (
          <button
            type="button"
            className="int__btn int__btn--quiet"
            onClick={() => onConfirm(true)}
          >
            {t('integrations.disconnect_button')}
          </button>
        ) : (
          <>
            <p className="int__body">{t('integrations.disconnect_desc')}</p>

            <div className="int__choices">
              <button
                type="button"
                className="int__btn int__btn--quiet"
                disabled={cut.isPending}
                onClick={() => cut.mutate('keep')}
              >
                {t('integrations.disconnect_keep')}
              </button>
              <button
                type="button"
                className="int__btn int__btn--danger"
                disabled={cut.isPending}
                onClick={() => cut.mutate('remove')}
              >
                {t('integrations.disconnect_remove')}
              </button>
              <button type="button" className="int__ghost" onClick={() => onConfirm(false)}>
                {t('integrations.cancel')}
              </button>
            </div>
          </>
        )}
      </section>
    </>
  );
}
